/**
 * 工作流步骤能力目录（前端镜像）。
 * 与 Java `WorkflowStepCatalog` 必须保持一致：新增/调整算子时两边一起改，并跑契约测试。
 *
 * kind:
 * - server  关页后 Worker 可独立完成
 * - client  必须浏览器（画布 / 交互 / 本机导出）
 * - retired 已下线
 * - hub     工作台页面，不是流水线算子
 */
export const WORKFLOW_STEP_KIND = Object.freeze({
  SERVER: 'server',
  CLIENT: 'client',
  RETIRED: 'retired',
  HUB: 'hub',
});

const SPECS = Object.freeze([
  { navId: 'imageCrop', label: '图案裁剪', kind: 'server', updatesPipelineImage: true, aliases: ['crop', 'image_crop'] },
  { navId: 'imageEnhance', label: '超分提质', kind: 'server', updatesPipelineImage: true, aliases: ['enhance', 'image_enhance', 'podImageClarity'] },
  { navId: 'smartCutout', label: '智能抠图', kind: 'server', updatesPipelineImage: true, aliases: ['cutout', 'smart_cutout'] },
  { navId: 'stampExtract', label: '印花提取', kind: 'server', updatesPipelineImage: true, aliases: ['stamp', 'stamp_extract', 'stampextract'] },
  { navId: 'goods', label: '主图创作', kind: 'server', updatesPipelineImage: true, aliases: ['productGoods', 'goodsStudio'] },
  { navId: 'img2img', label: '万能改图', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'aiOutpaint', label: 'AI扩图', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'aiTextToImage', label: '文生图', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'imageFission', label: '图片裂变', kind: 'server', updatesPipelineImage: true, aliases: ['fission'] },
  { navId: 'patternFission', label: '图案裂变', kind: 'server', updatesPipelineImage: true, aliases: ['pattern_fission'] },
  { navId: 'modelTryOn', label: '模特试衣', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'modelHandheld', label: '手持商品', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'modelPoseFission', label: '姿势裂变', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'modelUniversalWear', label: '万物穿戴', kind: 'server', updatesPipelineImage: true, aliases: [] },
  { navId: 'aiCopywriting', label: 'AI文案', kind: 'server', updatesPipelineImage: false, aliases: ['title', 'titles', 'titleExtract', 'title_extract', 'titleextract'] },
  { navId: 'videos', label: '图生视频', kind: 'server', updatesPipelineImage: false, aliases: [] },
  { navId: 'textToVideo', label: '文生视频', kind: 'server', updatesPipelineImage: false, aliases: ['texttovideo', 'text_to_video'] },
  { navId: 'infringementDetection', label: '侵权检测', kind: 'server', updatesPipelineImage: false, aliases: ['infringement', 'infringement_detection'] },
  {
    navId: 'platformListing',
    label: '批量上架',
    kind: 'server',
    updatesPipelineImage: false,
    aliases: [
      'listing',
      'platform_listing',
      'platformListingJobs',
      'platform_listing_jobs',
      'platformlistingjobs',
      'listingjobs',
      'listing_jobs',
      // 「定时上架」与「批量上架」共用一个算子：带 listingAutoRuleId 时后端路由到定时库
      'platformScheduledListings',
      'platform_scheduled_listings',
      'scheduled_listing',
    ],
    reason: '表格导出版本仍需浏览器',
  },
  {
    navId: 'podProductDesign',
    label: 'POD商品设计',
    kind: 'client',
    updatesPipelineImage: false,
    aliases: ['productcomposite', 'product_composite'],
    reason: '步骤「POD商品设计」需浏览器画布合成（含印花位分配），已排队等待客户端续跑',
  },
  { navId: 'sourcingHub', label: '插件采集', kind: 'hub', updatesPipelineImage: false, aliases: ['插件采集'] },
  { navId: 'imageCollection', label: '素材采集', kind: 'hub', updatesPipelineImage: false, aliases: [] },
  { navId: 'thirdPartyReceive', label: '第三方商品库', kind: 'hub', updatesPipelineImage: false, aliases: [] },
  { navId: 'ipeWorkflow', label: '工作流', kind: 'hub', updatesPipelineImage: false, aliases: [] },
  { navId: 'ipeWorkflowTasks', label: '工作流任务', kind: 'hub', updatesPipelineImage: false, aliases: [] },
]);

const BY_NAV = new Map(SPECS.map((spec) => [spec.navId, spec]));
const ALIAS_TO_NAV = new Map();
SPECS.forEach((spec) => {
  ALIAS_TO_NAV.set(spec.navId.toLowerCase(), spec.navId);
  (spec.aliases || []).forEach((alias) => {
    if (alias) ALIAS_TO_NAV.set(String(alias).toLowerCase(), spec.navId);
  });
});

export const WORKFLOW_STEP_CATALOG = SPECS;

export const SERVER_WORKFLOW_NAV_IDS = Object.freeze(
  SPECS.filter((spec) => spec.kind === WORKFLOW_STEP_KIND.SERVER).map((spec) => spec.navId),
);

export const SERVER_WORKFLOW_NAV_ID_SET = new Set(SERVER_WORKFLOW_NAV_IDS);

export function canonicalizeWorkflowStepNavId(navId) {
  const id = String(navId || '').trim();
  if (!id) return '';
  if (BY_NAV.has(id)) return id;
  return ALIAS_TO_NAV.get(id.toLowerCase()) || id;
}

export function findWorkflowStepSpec(navId) {
  const canonical = canonicalizeWorkflowStepNavId(navId);
  return BY_NAV.get(canonical) || null;
}

export function isWorkflowServerNav(navId) {
  return SERVER_WORKFLOW_NAV_ID_SET.has(canonicalizeWorkflowStepNavId(navId));
}

/** 契约快照：供前后端单测对齐，改目录时两边断言同一组 navId。 */
export function workflowStepCatalogContract() {
  return SPECS.map((spec) => `${spec.navId}|${spec.kind}`).sort();
}
